import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";
import syntraSymbol from "@/assets/syntra-symbol.svg";
import { SIGN_IN_PATH } from "@/lib/auth-routes";
import { firstPasswordError, isStrongPassword } from "@/lib/password";
import { PasswordField } from "@/components/auth/PasswordField";
import {
  PasswordMatchHint,
  PasswordRequirements,
} from "@/components/auth/PasswordRequirements";

interface InvitePreview {
  email: string;
  workspace_name: string;
  role: string;
  user_exists: boolean;
}

type Step = "loading" | "signup" | "login-required" | "error";

async function previewInvite(token: string): Promise<InvitePreview> {
  const { data, error } = await supabase.functions.invoke("accept-invite", {
    body: { action: "preview", token },
  });
  if (error) {
    const body = await (error as { context?: Response }).context
      ?.json()
      .catch(() => null);
    throw new Error(body?.error ?? error.message);
  }
  if (data?.error) throw new Error(data.error);
  return data as InvitePreview;
}

async function acceptInvite(token: string, fullName?: string): Promise<void> {
  const { data, error } = await supabase.functions.invoke("accept-invite", {
    body: {
      action: "accept",
      token,
      ...(fullName ? { full_name: fullName } : {}),
    },
  });
  if (error) {
    const body = await (error as { context?: Response }).context
      ?.json()
      .catch(() => null);
    throw new Error(body?.error ?? error.message);
  }
  if (data?.error) throw new Error(data.error);
}

async function signupForInvite(
  token: string,
  password: string,
  fullName: string,
): Promise<void> {
  const { data, error } = await supabase.functions.invoke("accept-invite", {
    body: { action: "signup", token, password, full_name: fullName },
  });
  if (error) {
    const body = await (error as { context?: Response }).context
      ?.json()
      .catch(() => null);
    throw new Error(body?.error ?? error.message);
  }
  if (data?.error) throw new Error(data.error);
}

export default function AcceptInvite() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";

  const [step, setStep] = useState<Step>("loading");
  const [preview, setPreview] = useState<InvitePreview | null>(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [fullName, setFullName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      navigate("/dashboard");
      return;
    }

    previewInvite(token)
      .then((data) => {
        setPreview(data);
        // Always route by invite identity; ignore any pre-existing session.
        // The signup/login forms below will authenticate as the invite email
        // before calling accept.
        if (data.user_exists) {
          setStep("login-required");
        } else {
          setStep("signup");
        }
      })
      .catch((err) => {
        setErrorMsg((err as Error).message);
        setStep("error");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!preview) return;

    const ruleError = firstPasswordError(password);
    if (ruleError) {
      toast.error(ruleError);
      return;
    }
    if (password !== confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }

    setSubmitting(true);
    try {
      // Possession of the invite token already proves the user owns this email,
      // so the edge function creates the account with email_confirm: true.
      await signupForInvite(token, password, fullName);

      const { error: signInErr } = await supabase.auth.signInWithPassword({
        email: preview.email,
        password,
      });
      if (signInErr) throw signInErr;

      await acceptInvite(token, fullName);
      toast.success(`You've joined ${preview.workspace_name}!`);
      navigate("/dashboard");
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!preview) return;
    setSubmitting(true);
    try {
      // Sign in as the invite email — replaces any existing session.
      const { error: signInErr } = await supabase.auth.signInWithPassword({
        email: preview.email,
        password,
      });
      if (signInErr) throw signInErr;

      await acceptInvite(token);
      toast.success(`You've joined ${preview.workspace_name}!`);
      navigate("/dashboard");
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  const workspaceList = preview?.workspace_name ?? "a workspace";
  const roleLabel = preview?.role === "admin" ? "Admin" : "Team Member";

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-md space-y-6">
        <div className="flex justify-center">
          <img src={syntraSymbol} alt="Syntra" className="h-10 w-10" />
        </div>

        {step === "loading" && (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        )}

        {step === "error" && (
          <Card>
            <CardHeader className="text-center">
              <CardTitle>Invalid invitation</CardTitle>
              <CardDescription>{errorMsg}</CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => navigate(SIGN_IN_PATH)}
              >
                Go to sign in
              </Button>
            </CardContent>
          </Card>
        )}

        {step === "signup" && (
          <Card>
            <CardHeader className="text-center">
              <CardTitle>Create your account</CardTitle>
              <CardDescription>
                You've been invited to join <strong>{workspaceList}</strong> as
                a{" "}
                <span className="font-medium text-foreground">{roleLabel}</span>
                .
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSignup} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="full-name">Full name</Label>
                  <Input
                    id="full-name"
                    placeholder="Jane Smith"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="invite-email">Email</Label>
                  <Input
                    id="invite-email"
                    value={preview?.email ?? ""}
                    disabled
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="password">Create a password</Label>
                  <PasswordField
                    id="password"
                    autoComplete="new-password"
                    placeholder="Enter a strong password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </div>

                <PasswordRequirements value={password} />

                <div className="space-y-1.5">
                  <Label htmlFor="confirm-password">Confirm password</Label>
                  <PasswordField
                    id="confirm-password"
                    autoComplete="new-password"
                    placeholder="Re-enter your password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    invalid={!!confirmPassword && confirmPassword !== password}
                    required
                  />
                  <PasswordMatchHint
                    password={password}
                    confirm={confirmPassword}
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full gradient-primary text-white"
                  disabled={
                    submitting ||
                    !fullName.trim() ||
                    !isStrongPassword(password) ||
                    password !== confirmPassword
                  }
                >
                  {submitting ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : null}
                  Create account & join
                  {!submitting && <ArrowRight className="h-4 w-4 ml-2" />}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {step === "login-required" && (
          <Card>
            <CardHeader className="text-center">
              <CardTitle>Sign in to accept</CardTitle>
              <CardDescription>
                Sign in as <strong>{preview?.email}</strong> to join{" "}
                {workspaceList} as a{" "}
                <span className="font-medium text-foreground">{roleLabel}</span>
                .
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="login-email">Email</Label>
                  <Input
                    id="login-email"
                    value={preview?.email ?? ""}
                    disabled
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="login-password">Password</Label>
                  <PasswordField
                    id="login-password"
                    autoComplete="current-password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </div>
                <Button type="submit" className="w-full" disabled={submitting}>
                  {submitting && (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  )}
                  Sign in & join
                </Button>
              </form>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
